import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { LevelBadge } from './LevelBadge';
import { useDb } from '../../hooks/useDb';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { traceById } from '../../utils/dbSelectors';
import type { Trace } from '../../types/db';
interface WhyPopoverProps {
  traceId: string;
  align?: 'left' | 'right';
  /** "wall" opens a centred floating trace readable from 2–3 m */
  size?: 'sm' | 'wall';
}
const EASE = [0.23, 1, 0.32, 1] as const;
function TraceSteps({
  trace,
  wall
}: {
  trace: Trace;
  wall: boolean;
}) {
  const steps = [{
    label: 'Aanleiding',
    body: `${trace.trigger} · ${trace.time}`
  }, {
    label: 'Context',
    body: [`Huismodus ${trace.mode}`, trace.presence, `Agenda: ${trace.calendar}`].join(' · ')
  }, {
    label: 'Doel',
    body: trace.goal
  }, {
    label: 'Gebruikte data',
    body: trace.data.map(d => `${d.label} (${d.where})`).join(' · ')
  }];
  const text = wall ? 'text-[22px]' : 'text-[14px]';
  const label = wall ? 'text-[22px]' : 'text-[12px]';
  return <ol className={`relative ${wall ? 'space-y-5' : 'space-y-3'}`} data-id="element-1398" data-mp="33y2hly0t8scu">
      <span aria-hidden className="absolute bottom-2 left-[5px] top-2 w-px bg-white/10" data-id="element-1399" data-mp="33voohupoxq7x" />
      {steps.map(s => <li key={s.label} className="relative pl-7" data-id="element-1400" data-mp="2uwun7r1f0bo1">
          <span aria-hidden className="absolute left-0 top-2 h-[11px] w-[11px] rounded-full border border-accent/60 bg-night-900" data-id="element-1401" data-mp="2uv9qburmogn0" />
          <p className={`font-medium text-ink-muted ${label}`} data-id="element-1402" data-mp="2ztl1c8tnsx6t">{s.label}</p>
          <p className={`leading-snug text-ink ${text}`} data-id="element-1403" data-mp="2l0hq0j6nvfoj">{s.body}</p>
        </li>)}
      <li className="relative pl-7" data-id="element-1404" data-mp="2py9t89crao81">
        <span aria-hidden className="absolute left-0 top-2 h-[11px] w-[11px] rounded-full border border-accent bg-accent" data-id="element-1405" data-mp="j7adtimche6v" />
        <p className={`font-medium text-ink-muted ${label}`} data-id="element-1406" data-mp="o6wobkasggbz">Gekozen actie en niveau</p>
        <p className={`mb-2 leading-snug text-ink ${text}`} data-id="element-1407" data-mp="moy61ebaacfx">{trace.action}</p>
        <LevelBadge level={trace.level} size={wall ? 'wall' : 'sm'} data-id="element-1408" data-mp="mmv5i0ci90wy" />
      </li>
    </ol>;
}

/** "Waarom?" — trigger → context → goal → data → chosen action + level. */
export function WhyPopover({
  traceId,
  align = 'right',
  size = 'sm'
}: WhyPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const reduce = useMotionPreference();
  const trace = useDb(t => traceById(t, traceId), [traceId]);
  const wall = size === 'wall';
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: PointerEvent) => {
      if (!wall && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, wall]);
  if (!trace) return null;
  const header = <div className="mb-4 flex items-center justify-between gap-4" data-id="element-1409" data-mp="3b251o9b18kk3">
      <p className={`font-semibold text-ink ${wall ? 'text-[26px]' : 'text-[13px]'}`} data-id="element-1410" data-mp="3g2j49btv72aq">Waarom?</p>
      <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="Sluiten" className={`grid place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/10 hover:text-ink ${wall ? 'h-12 w-12' : 'h-7 w-7'}`} data-id="element-1411" data-mp="3l1mav0phtgkt">
        <XIcon className={wall ? 'h-6 w-6' : 'h-4 w-4'} data-id="element-1412" data-mp="2ltk8kj3g6os" />
      </button>
    </div>;
  const panelMotion = {
    initial: reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.96,
      y: 6
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0
    },
    exit: reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.96,
      y: 6
    },
    transition: {
      duration: 0.18,
      ease: EASE
    }
  };
  return <div ref={ref} className="relative inline-block" data-id="element-1413" data-mp="3q5ep96keli9t">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(v => !v)} className={`whitespace-nowrap rounded-md px-1 font-semibold text-accent underline decoration-accent/40 underline-offset-4 transition-colors duration-150 hover:decoration-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${wall ? 'py-2 text-[22px]' : 'text-[13px]'}`} data-id="element-1414" data-mp="3q4lg5yxz2goi">
        Waarom?
      </button>

      {wall ? createPortal(<AnimatePresence data-id="element-1415" data-mp="3h3nirzi1h7ew">
              {open && <motion.div className="fixed inset-0 z-[60] grid place-items-center bg-night-950/70 p-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18
      }} onClick={() => setOpen(false)} data-id="element-1416" data-mp="3h69qq08udoxn">
                  <motion.div id={id} role="dialog" aria-modal="true" aria-label="Waarom deed Anna dit?" {...panelMotion} onClick={e => e.stopPropagation()} className="max-h-[85vh] w-full max-w-[640px] overflow-y-auto rounded-[28px] border border-white/10 bg-night-900 p-8" data-id="element-1417" data-mp="18xd0ojp2wknj">
                    {header}
                    <TraceSteps trace={trace} wall data-id="element-1418" data-mp="1dvwqwwapgilp" />
                  </motion.div>
                </motion.div>}
            </AnimatePresence>, document.body) : <AnimatePresence data-id="element-1419" data-mp="25mau9yl3ecj4">
            {open && <motion.div id={id} role="dialog" aria-label="Waarom deed Anna dit?" {...panelMotion} className={`absolute bottom-full z-30 mb-2 w-[340px] rounded-2xl border border-white/10 bg-night-900 p-4 text-left ${align === 'right' ? 'right-0 origin-bottom-right' : 'left-0 origin-bottom-left'}`} data-id="element-1420" data-mp="25lj81eddxr5i">
                {header}
                <TraceSteps trace={trace} wall={false} data-id="element-1421" data-mp="26ajqykklrjcn" />
              </motion.div>}
          </AnimatePresence>}
    </div>;
}